import { useState } from 'react'
import { Button, Input } from '@pilot-hub/ui'
import { Check, Pencil, Trash2, X } from 'lucide-react'
import { ORG_FARBE, ORG_NAME, symbolDef, symbolLabel, symbolSvg, type Organisation } from './taktischeSymbole'

/** Die Felder eines gesetzten Zeichens, die Info-Karte und Kartensymbol brauchen */
export interface TaktikZeichen {
  typ: string
  /** Text im Zeichen, z. B. „RTW“ */
  kennung: string
  /** Fahrzeugtyp o. Ä. – Zusatzinfo, z. B. "GW-Drohne" */
  bezeichnung?: string
  /** Funkrufname, wird unter dem Zeichen angezeigt */
  rufname?: string
  /** Farbe nach Organisation; leer = Standard des Zeichens */
  organisation?: Organisation
  /** Trupp aus dem Kräfte-Management, aus dem das Zeichen gesetzt wurde (Name/Typ/Mitglieder live) */
  truppKey?: string
}

/** Live-Angaben zu einem Trupp (aus dem aktuellen Kräfte-Management) */
export interface TruppInfo {
  name: string
  typLabel?: string
  fuehrer?: string
  mitglieder: Array<{ name: string; fahrzeug?: string; fuehrer?: boolean }>
}
/** Trupp-Daten eines Zeichens: Live-Info oder 'aufgeloest' (Trupp existiert nicht mehr) */
export type TruppAnzeige = TruppInfo | 'aufgeloest'

/** Trupp-Block (Tooltip und Info-Karte): Truppführer hervorgehoben, Mitglieder mit Fahrzeug-Funkrufname */
function TruppBlock({ t, hell }: { t: TruppAnzeige; hell: boolean }) {
  const dim = hell ? 'opacity-60' : 'text-dark-text-subtle'
  if (t === 'aufgeloest') {
    return <div className={`mt-1 font-semibold ${hell ? 'text-red-700' : 'text-amber-300'}`}>Trupp aufgelöst</div>
  }
  return (
    <div className={hell ? 'mt-1 border-t border-black/15 pt-1' : 'mt-3 border-t border-dark-border pt-2'}>
      <div className={`font-semibold ${hell ? '' : 'text-dark-text'}`}>{t.name}{t.typLabel ? <span className={`font-normal ${dim}`}> · {t.typLabel}</span> : null}</div>
      <div className={`mt-0.5 font-bold ${hell ? 'text-amber-700' : 'text-amber-300'}`}>Truppführer: {t.fuehrer ?? '–'}</div>
      <div className={`mt-1 ${dim}`}>Mitglieder ({t.mitglieder.length}):</div>
      <ul className={hell ? '' : 'max-h-40 overflow-y-auto'}>
        {t.mitglieder.map((m, i) => (
          <li key={i} className={hell ? '' : 'text-dark-text'}>
            <span className={m.fuehrer ? 'font-bold' : ''}>{m.name}{m.fuehrer ? ' (TF)' : ''}</span>
            {m.fahrzeug && <span className={dim}> – {m.fahrzeug}</span>}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Gerät mit Maus/Trackpad: nur dort gibt es Hover. Auf Tablets ersetzt Antippen den Hover. */
export const hatMaus = typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** HTML für das Leaflet-DivIcon: Zeichen, darunter optional der Rufname */
export function symbolIconHtml(s: TaktikZeichen, aktiv: boolean, mitRufname: boolean): string {
  const schatten = aktiv ? 'drop-shadow(0 0 4px #f59e0b) drop-shadow(0 0 2px #f59e0b)' : 'drop-shadow(0 1px 2px rgba(0,0,0,.4))'
  const ruf = mitRufname && s.rufname
    ? `<div style="position:absolute;left:50%;top:41px;transform:translateX(-50%);white-space:nowrap;font:700 10px/13px Arial,Helvetica,sans-serif;color:#141414;background:rgba(255,255,255,.94);border:1px solid #141414;border-radius:3px;padding:0 4px">${esc(s.rufname)}</div>`
    : ''
  return `<div style="position:relative;width:52px;height:40px;filter:${schatten}">${symbolSvg(s.typ, s.kennung, s.organisation)}${ruf}</div>`
}

/** Zeilen für Tooltip und Info-Karte */
export function symbolInfoZeilen(s: TaktikZeichen): Array<[string, string]> {
  const def = symbolDef(s.typ)
  const zeilen: Array<[string, string]> = []
  if (s.rufname) zeilen.push(['Rufname', s.rufname])
  if (s.bezeichnung && !s.truppKey && s.bezeichnung !== def?.label) zeilen.push(['Fahrzeug', s.bezeichnung])
  if (s.kennung) zeilen.push(['Im Zeichen', s.kennung])
  if (s.organisation) zeilen.push(['Organisation', ORG_NAME[s.organisation]])
  if (def?.quelle) zeilen.push(['Grundlage', def.quelle.startsWith('abgeleitet') ? def.quelle : `DV 102 ${def.quelle}`])
  return zeilen
}

/** Inhalt des Hover-Tooltips (nur Geräte mit Maus) */
export function SymbolTooltip({ s, trupp }: { s: TaktikZeichen; trupp?: TruppAnzeige }) {
  return (
    <div className="text-[11px] leading-snug">
      <div className="font-semibold">{symbolLabel(s.typ)}</div>
      {symbolInfoZeilen(s).map(([k, v]) => (
        <div key={k}><span className="opacity-60">{k}:</span> {v}</div>
      ))}
      {trupp && <TruppBlock t={trupp} hell />}
    </div>
  )
}

/**
 * Info-Karte zu einem angetippten/angeklickten Zeichen. Ersetzt auf Tablets den Hover:
 * erst Informationen, „Bearbeiten“ öffnet die Felder.
 */
export function SymbolInfoKarte({ s, trupp, nurLesen, onAendern, onLoeschen, onSchliessen, positionCls = 'right-3 top-3 z-20' }: {
  /** Position innerhalb der Kartenbühne (Tailwind-Klassen) */
  positionCls?: string
  s: TaktikZeichen
  /** Live-Daten des Trupps, wenn das Zeichen aus einem Trupp gesetzt wurde */
  trupp?: TruppAnzeige
  nurLesen?: boolean
  onAendern: (patch: Partial<TaktikZeichen>) => void
  onLoeschen: () => void
  onSchliessen: () => void
}) {
  const [bearbeiten, setBearbeiten] = useState(false)
  return (
    <div className={`absolute ${positionCls} w-[300px] max-w-[calc(100%-24px)] rounded-xl border border-dark-border bg-dark-bg-secondary/95 p-3 shadow-2xl backdrop-blur`}>
      <div className="mb-2 flex items-start gap-3">
        <span className="shrink-0 rounded-md bg-white p-1" dangerouslySetInnerHTML={{ __html: symbolSvg(s.typ, s.kennung, s.organisation) }} />
        <span className="min-w-0 flex-1 pt-1 text-sm font-semibold leading-tight text-dark-text">{symbolLabel(s.typ)}</span>
        <button onClick={onSchliessen} aria-label="Schließen"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-dark-text-muted hover:bg-dark-bg-tertiary">
          <X className="h-4 w-4" />
        </button>
      </div>

      {!bearbeiten ? (
        <>
          <dl className="space-y-1 text-xs">
            {symbolInfoZeilen(s).map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <dt className="w-[84px] shrink-0 text-dark-text-subtle">{k}</dt>
                <dd className="min-w-0 break-words text-dark-text">
                  {k === 'Organisation' && s.organisation && (
                    <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm border border-dark-border align-middle" style={{ background: ORG_FARBE[s.organisation] }} />
                  )}
                  {v}
                </dd>
              </div>
            ))}
          </dl>
          {trupp && <div className="text-xs"><TruppBlock t={trupp} hell={false} /></div>}
          {!nurLesen && (
            <div className="mt-3 flex gap-2">
              <Button variant="primary" className="flex-1 min-h-[44px]" onClick={() => setBearbeiten(true)}>
                <Pencil className="mr-2 h-4 w-4" /> Bearbeiten
              </Button>
              <Button variant="danger" className="min-h-[44px]" onClick={onLoeschen} aria-label="Zeichen löschen">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="label" htmlFor="tz-rufname">Rufname (unter dem Zeichen)</label>
            <Input id="tz-rufname" value={s.rufname ?? ''} onChange={e => onAendern({ rufname: e.target.value })} className="mt-1" placeholder="z. B. Sama 56-79-1" />
          </div>
          {!s.truppKey && <div>
            <label className="label" htmlFor="tz-kennung">Text im Zeichen</label>
            <Input id="tz-kennung" value={s.kennung} onChange={e => onAendern({ kennung: e.target.value })} className="mt-1" placeholder="z. B. RTW" />
          </div>}
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1 min-h-[44px]" onClick={() => setBearbeiten(false)}>
              <Check className="mr-2 h-4 w-4" /> Fertig
            </Button>
            <Button variant="danger" className="min-h-[44px]" onClick={onLoeschen} aria-label="Zeichen löschen">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

const LIVE_ROT = '#d9282f'

/**
 * Live-Drohne: taktisches Zeichen „Drohne, Drehflügler“ (DV 102 3.4 + 5.1.4.2) aufrecht auf heller
 * Scheibe, darum ein gestrichelter Kursring; die Marke auf dem Ring zeigt die Ausrichtung (Kurs in Grad).
 * Zeichenfläche 96 × 96, Mittelpunkt (48, 48).
 */
export function liveDrohneSvg(kurs: number | undefined | null, groesse = 80): string {
  const zeichen = symbolSvg('drohne').replace(/<svg [^>]*?viewBox="0 0 52 40" width="52" height="40"/, '<svg x="24.6" y="30" width="46.8" height="36" viewBox="0 0 52 40"')
  const marke = kurs === undefined || kurs === null ? '' :
    `<g transform="rotate(${kurs} 48 48)"><path d="M48 11 L54.5 24 L48 20.5 L41.5 24 Z" fill="${LIVE_ROT}" stroke="#fff" stroke-width="1.6" stroke-linejoin="round"/></g>`
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" width="${groesse}" height="${groesse}">` +
    `<circle cx="48" cy="48" r="27" fill="none" stroke="#fff" stroke-width="4.5" stroke-opacity=".9"/>` +
    `<circle cx="48" cy="48" r="27" fill="none" stroke="${LIVE_ROT}" stroke-width="1.6" stroke-dasharray="2 4"/>` +
    marke +
    `<circle cx="48" cy="48" r="17.5" fill="#fff" fill-opacity=".9" stroke="${LIVE_ROT}" stroke-width="1.6"/>` +
    zeichen + `</svg>`
}

/* ------------------------------------------------------------------ Trupps */

/** Trupp aus kraefte_management.data.trupps */
export interface TruppRoh { key: string; name: string; typ: string; fuehrerId: number | null; mitglieder: number[] }

export function truppsAuswerten(d: any): TruppRoh[] {
  // Nur aktive Trupps (aufgelöste = ohne Mitglieder werden nicht angezeigt; Reserve bleibt sichtbar)
  return ((d?.trupps ?? []) as any[]).filter(t => t?.key && (t.mitglieder ?? []).length > 0).map(t => ({
    key: String(t.key), name: String(t.name ?? ''), typ: String(t.typ ?? 'drohnentrupp'),
    fuehrerId: t.fuehrerId != null ? Number(t.fuehrerId) : null,
    mitglieder: ((t.mitglieder ?? []) as any[]).map(Number),
  }))
}

/** Nutzer-ID → Funkrufname des Fahrzeugs, auf dem die Person im Kräfte-Management eingeteilt ist */
export function fahrzeugRufnamenJeNutzer(d: any, rufname: (fahrzeugId: number) => string | undefined): Record<number, string> {
  const out: Record<number, string> = {}
  for (const b of d?.bloecke ?? []) {
    if (b?.fahrzeugId == null) continue
    const r = rufname(Number(b.fahrzeugId))
    if (!r) continue
    for (const m of b.besatzung ?? []) if (m?.nutzerId != null && out[Number(m.nutzerId)] == null) out[Number(m.nutzerId)] = r
  }
  return out
}

/** Zeichen mit den aktuellen Trupp-Angaben (Name als Kennung, Typ); Zeichen ohne/aufgelöster Trupp bleibt unverändert */
export function truppZeichen<T extends TaktikZeichen>(s: T, trupps: TruppRoh[]): T {
  if (!s.truppKey) return s
  const t = trupps.find(x => x.key === s.truppKey)
  if (!t) return s
  return { ...s, typ: symbolDef(t.typ) ? t.typ : s.typ, kennung: t.name }
}

/** Live-Info zum Trupp eines Zeichens; undefined, wenn das Zeichen nicht aus einem Trupp stammt */
export function truppAnzeige(s: TaktikZeichen, trupps: TruppRoh[], namen: Record<number, string>, rufnamen: Record<number, string>): TruppAnzeige | undefined {
  if (!s.truppKey) return undefined
  const t = trupps.find(x => x.key === s.truppKey)
  if (!t) return 'aufgeloest'
  const name = (id: number) => namen[id] ?? `Nutzer #${id}`
  return {
    name: t.name,
    typLabel: symbolLabel(t.typ),
    fuehrer: t.fuehrerId != null ? name(t.fuehrerId) : undefined,
    mitglieder: t.mitglieder.map(id => ({ name: name(id), fahrzeug: rufnamen[id], fuehrer: id === t.fuehrerId })),
  }
}
